/**
 * The **Favorites** pane (`specs/M1-B10-shell-chrome.md` §4.1).
 *
 * The rail's Favorites slot swapped only the pane's *title* before this round — the tree it
 * showed was still the workspace's. monday's pane (`[c:rail-favorites]`) is a flat list of the
 * favourited entities at the same **36 px** row pitch as the workspace tree, starting directly
 * under the pane header with no workspace switcher and no `Manage workspace` row.
 *
 * A favourite is per user (B1 §5.6), so this list is never shared and never cached across
 * users.
 */
import { EntityIcon, IconButton, PageEmptyState, SidebarRow, boardEntityKind, icons } from '@elizmonday/ui';
import { useNavigate } from 'react-router';
import { useTRPCClient } from '../../lib/trpc';
import { entityUrl } from '../shell/urls';
import { useBoardInvalidation } from './hooks';
import { useFavorites } from './favorites';

type FavoriteRow = {
  entityType: string;
  entityId: string | number;
  name?: string | null;
  kind?: string | null;
};

export function FavoritesPane() {
  const favorites = useFavorites();
  const client = useTRPCClient();
  const invalidate = useBoardInvalidation();
  const navigate = useNavigate();

  const rows = (favorites.data ?? []) as FavoriteRow[];

  if (favorites.isPending) {
    return <div className="ez-pane__tree" aria-busy="true" data-testid="favorites-pane" />;
  }

  if (rows.length === 0) {
    return (
      <div className="ez-pane__tree" data-testid="favorites-pane">
        <PageEmptyState
          title="No favorites yet"
          hint="Star a board in the tree and it appears here."
          data-testid="favorites-empty"
        />
      </div>
    );
  }

  const remove = async (row: FavoriteRow) => {
    await client.favorites.remove.mutate({
      entityType: row.entityType as 'board' | 'doc' | 'dashboard' | 'workspace' | 'folder',
      entityId: String(row.entityId),
    });
    await favorites.refetch();
    await invalidate();
  };

  return (
    <div className="ez-pane__tree" data-testid="favorites-pane">
      <div role="tree" aria-label="Favorites" className="ez-pane__tree-inner">
        {rows.map((row) => {
          const id = String(row.entityId);
          const type = row.entityType === 'doc' || row.entityType === 'dashboard' ? row.entityType : 'board';
          const name = row.name ?? id;
          return (
            <SidebarRow
              key={`${row.entityType}-${id}`}
              label={name}
              icon={
                row.entityType === 'doc' ? (
                  <EntityIcon kind="doc" />
                ) : row.entityType === 'dashboard' ? (
                  <EntityIcon kind="dashboard" />
                ) : (
                  <EntityIcon kind={boardEntityKind(row.kind ?? 'public')} />
                )
              }
              onClick={() => navigate(entityUrl({ id, type }))}
              actions={
                <IconButton
                  icon={icons.favoriteFilled}
                  ariaLabel={`Remove ${name} from favorites`}
                  size="xs"
                  kind="tertiary"
                  active
                  data-testid={`favorite-remove-${id}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    void remove(row);
                  }}
                />
              }
              data-testid={`favorite-row-${id}`}
            />
          );
        })}
      </div>
    </div>
  );
}
